import React, { useState, useEffect } from "react";
import { db } from "./firebase";
import { collection, getDocs, deleteDoc, doc, orderBy, query } from "firebase/firestore";
import ThumbnailComponent from "./ThumbnailComponent";

const ReportDashboard = ({ onEditReport, onCreateNew }) => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusMessage, setStatusMessage] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("dateCreated");
  const [viewMode, setViewMode] = useState("grid"); // grid or list

  const showStatus = (message, type = "info") => {
    setStatusMessage({ text: message, type });
    setTimeout(() => setStatusMessage(""), 5000);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const calculateReportFileSize = (report) => {
    let totalSize = 0;
    
    if (report.sections) {
      report.sections.forEach(section => {
        if (section.images) {
          section.images.forEach(image => {
            // Use optimized size if available, otherwise use original size
            totalSize += image.optimizedSize || image.originalSize || 0;
          });
        }
      });
    }
    
    return totalSize;
  };

  const getFirstImageUrl = (report) => {
    if (!report.sections || report.sections.length === 0) return null;
    
    for (const section of report.sections) {
      if (section.images && section.images.length > 0) {
        return section.images[0].imageUrl;
      }
    }
    
    return null;
  };

  const getTotalImagesCount = (report) => {
    if (!report.sections) return 0;
    return report.sections.reduce((total, section) => 
      total + (section.images?.length || 0), 0
    );
  };

  const getOptimizationSavings = (report) => {
    let totalOriginal = 0;
    let totalOptimized = 0;
    
    if (report.sections) {
      report.sections.forEach(section => {
        if (section.images) {
          section.images.forEach(image => {
            totalOriginal += image.originalSize || image.optimizedSize || 0;
            totalOptimized += image.optimizedSize || image.originalSize || 0;
          });
        }
      });
    }
    
    if (totalOriginal > 0 && totalOriginal > totalOptimized) {
      const savings = ((totalOriginal - totalOptimized) / totalOriginal * 100).toFixed(1);
      return { savings, totalOriginal, totalOptimized };
    }
    
    return null;
  };

  const getStorageEfficiencyColor = (fileSize) => {
    if (fileSize < 1024 * 1024) return "#28a745"; // Green for < 1MB
    if (fileSize < 5 * 1024 * 1024) return "#ffc107"; // Yellow for < 5MB
    return "#dc3545"; // Red for > 5MB
  };

  const fetchReports = async () => {
    setLoading(true);
    try {
      showStatus("🔄 Loading reports...", "info");
      
      const q = query(collection(db, "reports"), orderBy("dateCreated", "desc"));
      const querySnapshot = await getDocs(q);
      
      const reportsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        dateCreated: doc.data().dateCreated?.toDate() || new Date()
      }));
      
      setReports(reportsData);
      showStatus(`✅ Loaded ${reportsData.length} reports`, "success");
    } catch (error) {
      console.error("Error fetching reports:", error);
      showStatus(`❌ Failed to load reports: ${error.message}`, "error");
    } finally {
      setLoading(false);
    }
  };

  const deleteReport = async (reportId, reportTitle) => {
    if (!window.confirm(`Are you sure you want to delete "${reportTitle}"? This action cannot be undone.`)) {
      return;
    }

    try {
      showStatus("🔄 Deleting report...", "info");
      await deleteDoc(doc(db, "reports", reportId));
      setReports(reports.filter(report => report.id !== reportId));
      showStatus("✅ Report deleted successfully", "success");
    } catch (error) {
      console.error("Error deleting report:", error);
      showStatus(`❌ Failed to delete report: ${error.message}`, "error");
    }
  };

  const duplicateReport = (report) => {
    const duplicatedReport = {
      ...report,
      title: `${report.title} (Copy)`,
      id: null // Will get new ID when saved
    };
    onEditReport(duplicatedReport);
  };

  useEffect(() => {
    fetchReports();
  }, []);

  const getStatusStyle = (type) => {
    const baseStyle = {
      padding: "10px",
      marginBottom: "20px",
      borderRadius: "5px",
      fontWeight: "bold"
    };
    
    switch (type) {
      case "success":
        return { ...baseStyle, backgroundColor: "#d4edda", color: "#155724", border: "1px solid #c3e6cb" };
      case "error":
        return { ...baseStyle, backgroundColor: "#f8d7da", color: "#721c24", border: "1px solid #f5c6cb" };
      case "warning":
        return { ...baseStyle, backgroundColor: "#fff3cd", color: "#856404", border: "1px solid #ffeaa7" };
      default:
        return { ...baseStyle, backgroundColor: "#d1ecf1", color: "#0c5460", border: "1px solid #bee5eb" };
    }
  };

  const filteredReports = reports.filter(report =>
    report.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    report.createdBy?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    report.clientName?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const sortedReports = [...filteredReports].sort((a, b) => {
    switch (sortBy) {
      case "title":
        return a.title?.localeCompare(b.title) || 0;
      case "createdBy":
        return a.createdBy?.localeCompare(b.createdBy) || 0;
      case "clientName":
        return a.clientName?.localeCompare(b.clientName) || 0;
      case "fileSize":
        return calculateReportFileSize(b) - calculateReportFileSize(a);
      case "imageCount":
        return getTotalImagesCount(b) - getTotalImagesCount(a);
      case "dateCreated":
      default:
        return new Date(b.dateCreated) - new Date(a.dateCreated);
    }
  });

  // Calculate total storage usage and statistics
  const totalStorageUsed = reports.reduce((total, report) => 
    total + calculateReportFileSize(report), 0
  );
  
  const totalImages = reports.reduce((total, report) => 
    total + getTotalImagesCount(report), 0
  );

  const averageReportSize = reports.length > 0 ? totalStorageUsed / reports.length : 0;

  return (
    <div style={{ padding: "20px", fontFamily: "Arial" }}>
      {/* GEP Header with Logo */}
      <div style={{ 
        display: "flex", 
        alignItems: "center", 
        marginBottom: "30px",
        padding: "20px",
        backgroundColor: "#f8f9fa",
        borderRadius: "10px",
        border: "2px solid #dee2e6"
      }}>
        <img 
          src="/GEP-Logo.png" 
          alt="GEP Contractors Logo" 
          style={{ 
            height: "50px", 
            marginRight: "20px",
            objectFit: "contain"
          }}
          onError={(e) => {
            e.target.style.display = "none";
          }}
        />
        <div style={{ flex: 1 }}>
          <h1 style={{ 
            margin: "0 0 5px 0", 
            color: "#333",
            fontSize: "24px",
            fontWeight: "bold"
          }}>
            📊 GEP Report Dashboard
          </h1>
          <p style={{ 
            margin: 0, 
            color: "#666",
            fontSize: "14px"
          }}>
            Manage and view all your photo reports
          </p>
        </div>
        <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
          {/* View Mode Toggle */}
          <div style={{ display: "flex", border: "1px solid #ddd", borderRadius: "5px", overflow: "hidden" }}>
            <button
              onClick={() => setViewMode("grid")}
              style={{
                backgroundColor: viewMode === "grid" ? "#007bff" : "white",
                color: viewMode === "grid" ? "white" : "#007bff",
                border: "none",
                padding: "8px 12px",
                cursor: "pointer",
                fontSize: "12px"
              }}
            >
              🔲 Grid
            </button>
            <button
              onClick={() => setViewMode("list")}
              style={{
                backgroundColor: viewMode === "list" ? "#007bff" : "white",
                color: viewMode === "list" ? "white" : "#007bff",
                border: "none",
                padding: "8px 12px",
                cursor: "pointer",
                fontSize: "12px"
              }}
            >
              📋 List
            </button>
          </div>
          
          <button 
            onClick={onCreateNew}
            style={{
              backgroundColor: "#28a745",
              color: "white",
              border: "none",
              padding: "12px 20px",
              borderRadius: "5px",
              fontSize: "16px",
              cursor: "pointer",
              fontWeight: "bold"
            }}
          >
            ➕ Create New Report
          </button>
        </div>
      </div>

      {/* Status Message */}
      {statusMessage && (
        <div style={getStatusStyle(statusMessage.type)}>
          {statusMessage.text}
        </div>
      )}

      {/* Search and Filter Controls */}
      <div style={{ 
        marginBottom: "20px", 
        display: "flex", 
        gap: "15px", 
        flexWrap: "wrap",
        alignItems: "center"
      }}>
        <input
          type="text"
          placeholder="🔍 Search reports by title, manager, or client..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            flex: 1,
            minWidth: "300px",
            padding: "10px",
            border: "1px solid #ddd",
            borderRadius: "5px",
            fontSize: "14px"
          }}
        />
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          style={{
            padding: "10px",
            border: "1px solid #ddd",
            borderRadius: "5px",
            fontSize: "14px"
          }}
        >
          <option value="dateCreated">Sort by Date (Newest)</option>
          <option value="title">Sort by Title</option>
          <option value="createdBy">Sort by Manager</option>
          <option value="clientName">Sort by Client</option>
          <option value="fileSize">Sort by File Size</option>
          <option value="imageCount">Sort by Image Count</option>
        </select>
        <button
          onClick={fetchReports}
          style={{
            backgroundColor: "#17a2b8",
            color: "white",
            border: "none",
            padding: "10px 15px",
            borderRadius: "5px",
            cursor: "pointer"
          }}
        >
          🔄 Refresh
        </button>
      </div>

      {/* Enhanced Statistics Dashboard */}
      <div style={{ 
        marginBottom: "20px", 
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
        gap: "15px"
      }}>
        <div style={{ 
          padding: "15px", 
          backgroundColor: "#f8f9fa", 
          borderRadius: "8px",
          border: "1px solid #dee2e6",
          textAlign: "center"
        }}>
          <div style={{ fontSize: "24px", fontWeight: "bold", color: "#007bff" }}>
            {filteredReports.length}
          </div>
          <div style={{ fontSize: "12px", color: "#666" }}>
            Reports {searchTerm && `(of ${reports.length})`}
          </div>
        </div>
        
        <div style={{ 
          padding: "15px", 
          backgroundColor: "#f8f9fa", 
          borderRadius: "8px",
          border: "1px solid #dee2e6",
          textAlign: "center"
        }}>
          <div style={{ fontSize: "18px", fontWeight: "bold", color: "#28a745" }}>
            {formatFileSize(totalStorageUsed)}
          </div>
          <div style={{ fontSize: "12px", color: "#666" }}>
            Total Storage
          </div>
        </div>
        
        <div style={{ 
          padding: "15px", 
          backgroundColor: "#f8f9fa", 
          borderRadius: "8px",
          border: "1px solid #dee2e6",
          textAlign: "center"
        }}>
          <div style={{ fontSize: "24px", fontWeight: "bold", color: "#ffc107" }}>
            {totalImages}
          </div>
          <div style={{ fontSize: "12px", color: "#666" }}>
            Total Images
          </div>
        </div>
        
        <div style={{ 
          padding: "15px", 
          backgroundColor: "#f8f9fa", 
          borderRadius: "8px",
          border: "1px solid #dee2e6",
          textAlign: "center"
        }}>
          <div style={{ fontSize: "18px", fontWeight: "bold", color: "#6f42c1" }}>
            {formatFileSize(averageReportSize)}
          </div>
          <div style={{ fontSize: "12px", color: "#666" }}>
            Avg Report Size
          </div>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div style={{ 
          textAlign: "center", 
          padding: "40px", 
          fontSize: "18px",
          color: "#666"
        }}>
          🔄 Loading reports...
        </div>
      )}

      {/* No Reports State */}
      {!loading && reports.length === 0 && (
        <div style={{ 
          textAlign: "center", 
          padding: "40px",
          backgroundColor: "#f8f9fa",
          borderRadius: "10px",
          border: "2px dashed #dee2e6"
        }}>
          <h3 style={{ color: "#666", marginBottom: "15px" }}>📝 No Reports Yet</h3>
          <p style={{ color: "#888", marginBottom: "20px" }}>
            Create your first GEP photo report to get started!
          </p>
          <button 
            onClick={onCreateNew}
            style={{
              backgroundColor: "#28a745",
              color: "white",
              border: "none",
              padding: "15px 25px",
              borderRadius: "5px",
              fontSize: "16px",
              cursor: "pointer",
              fontWeight: "bold"
            }}
          >
            ➕ Create First Report
          </button>
        </div>
      )}

      {/* No Search Results */}
      {!loading && reports.length > 0 && filteredReports.length === 0 && (
        <div style={{ 
          textAlign: "center", 
          padding: "40px",
          backgroundColor: "#fff3cd",
          borderRadius: "10px",
          border: "1px solid #ffeaa7"
        }}>
          <h3 style={{ color: "#856404", marginBottom: "15px" }}>🔍 No Results Found</h3>
          <p style={{ color: "#856404" }}>
            No reports match your search term "{searchTerm}"
          </p>
          <button 
            onClick={() => setSearchTerm("")}
            style={{
              backgroundColor: "#ffc107",
              color: "#212529",
              border: "none",
              padding: "10px 20px",
              borderRadius: "5px",
              cursor: "pointer",
              marginTop: "10px"
            }}
          >
            Clear Search
          </button>
        </div>
      )}

      {/* Reports Display */}
      {!loading && sortedReports.length > 0 && (
        <div style={viewMode === "grid" ? { 
          display: "grid", 
          gridTemplateColumns: "repeat(auto-fill, minmax(420px, 1fr))", 
          gap: "20px" 
        } : {
          display: "flex",
          flexDirection: "column",
          gap: "10px"
        }}>
          {sortedReports.map((report) => {
            const firstImageUrl = getFirstImageUrl(report);
            const totalFileSize = calculateReportFileSize(report);
            const imageCount = getTotalImagesCount(report);
            const optimizationStats = getOptimizationSavings(report);
            const storageColor = getStorageEfficiencyColor(totalFileSize);
            
            if (viewMode === "list") {
              // List View
              return (
                <div 
                  key={report.id} 
                  style={{
                    border: "1px solid #ddd",
                    borderRadius: "8px",
                    padding: "15px",
                    backgroundColor: "white",
                    display: "flex",
                    alignItems: "center",
                    gap: "15px",
                    transition: "background-color 0.2s"
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "#f8f9fa";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "white";
                  }}
                >
                  <ThumbnailComponent 
                    imageUrl={firstImageUrl}
                    reportTitle={report.title}
                    imageCount={imageCount}
                    size={60}
                  />
                  
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <h4 style={{ margin: "0 0 5px 0", fontSize: "16px" }}>
                      {report.title || "Untitled Report"}
                    </h4>
                    <div style={{ fontSize: "12px", color: "#666" }}>
                      {report.createdBy && `👤 ${report.createdBy} • `}
                      {report.clientName && `🏢 ${report.clientName} • `}
                      📅 {report.dateCreated?.toLocaleDateString()}
                    </div>
                  </div>
                  
                  <div style={{ textAlign: "right", fontSize: "12px" }}>
                    <div style={{ color: storageColor, fontWeight: "bold" }}>
                      💾 {formatFileSize(totalFileSize)}
                    </div>
                    <div style={{ color: "#666" }}>
                      📸 {imageCount} images
                    </div>
                  </div>
                  
                  <div style={{ display: "flex", gap: "5px" }}>
                    <button
                      onClick={() => onEditReport(report)}
                      style={{
                        backgroundColor: "#007bff",
                        color: "white",
                        border: "none",
                        padding: "6px 10px",
                        borderRadius: "4px",
                        fontSize: "11px",
                        cursor: "pointer"
                      }}
                    >
                      ✏️ Edit
                    </button>
                    <button
                      onClick={() => deleteReport(report.id, report.title)}
                      style={{
                        backgroundColor: "#dc3545",
                        color: "white",
                        border: "none",
                        padding: "6px 10px",
                        borderRadius: "4px",
                        fontSize: "11px",
                        cursor: "pointer"
                      }}
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              );
            }
            
            // Grid View (existing code with enhanced thumbnail)
            return (
              <div 
                key={report.id} 
                style={{
                  border: "1px solid #ddd",
                  borderRadius: "10px",
                  padding: "20px",
                  backgroundColor: "white",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                  transition: "transform 0.2s, box-shadow 0.2s"
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow = "0 4px 8px rgba(0,0,0,0.15)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "0 2px 4px rgba(0,0,0,0.1)";
                }}
              >
                {/* Thumbnail and Header Section */}
                <div style={{ 
                  display: "flex", 
                  gap: "15px", 
                  marginBottom: "15px",
                  alignItems: "flex-start"
                }}>
                  <ThumbnailComponent 
                    imageUrl={firstImageUrl}
                    reportTitle={report.title}
                    imageCount={imageCount}
                    size={80}
                  />

                  {/* Report Header Info */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <h3 style={{ 
                      margin: "0 0 8px 0", 
                      color: "#333",
                      fontSize: "16px",
                      fontWeight: "bold",
                      wordBreak: "break-word"
                    }}>
                      📋 {report.title || "Untitled Report"}
                    </h3>
                    <div style={{ 
                      fontSize: "11px", 
                      color: "#666",
                      marginBottom: "8px"
                    }}>
                      <div>ID: {report.id}</div>
                      <div>{report.dateCreated?.toLocaleDateString()}</div>
                    </div>
                    
                    {/* File Size and Image Count */}
                    <div style={{ 
                      display: "flex", 
                      gap: "8px", 
                      fontSize: "12px",
                      flexWrap: "wrap"
                    }}>
                      <span style={{ 
                        backgroundColor: storageColor, 
                        color: "white",
                        padding: "3px 8px", 
                        borderRadius: "12px",
                        fontWeight: "bold"
                      }}>
                        💾 {formatFileSize(totalFileSize)}
                      </span>
                      <span style={{ 
                        backgroundColor: "#e9ecef", 
                        padding: "3px 8px", 
                        borderRadius: "12px",
                        color: "#495057"
                      }}>
                        📸 {imageCount}
                      </span>
                      <span style={{ 
                        backgroundColor: "#e9ecef", 
                        padding: "3px 8px", 
                        borderRadius: "12px",
                        color: "#495057"
                      }}>
                        📂 {report.sections?.length || 0}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Report Details */}
                <div style={{ marginBottom: "15px", fontSize: "13px" }}>
                  {report.createdBy && (
                    <div style={{ marginBottom: "4px" }}>
                      <strong>👤 Manager:</strong> {report.createdBy}
                    </div>
                  )}
                  {report.clientName && (
                    <div style={{ marginBottom: "4px" }}>
                      <strong>🏢 Client:</strong> {report.clientName}
                    </div>
                  )}
                  {report.reportDate && (
                    <div style={{ marginBottom: "4px" }}>
                      <strong>📅 Report Date:</strong> {report.reportDate}
                    </div>
                  )}
                  
                  {/* Optimization Stats */}
                  {optimizationStats && (
                    <div style={{ 
                      marginTop: "8px",
                      padding: "6px",
                      backgroundColor: "#d4edda",
                      borderRadius: "4px",
                      fontSize: "11px",
                      color: "#155724"
                    }}>
                      🚀 Optimized: {optimizationStats.savings}% smaller 
                      ({formatFileSize(optimizationStats.totalOriginal)} → {formatFileSize(optimizationStats.totalOptimized)})
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div style={{ 
                  display: "flex", 
                  gap: "6px", 
                  flexWrap: "wrap" 
                }}>
                  <button
                    onClick={() => onEditReport(report)}
                    style={{
                      backgroundColor: "#007bff",
                      color: "white",
                      border: "none",
                      padding: "8px 12px",
                      borderRadius: "4px",
                      fontSize: "11px",
                      cursor: "pointer",
                      flex: 1
                    }}
                  >
                    ✏️ Edit
                  </button>
                  <button
                    onClick={() => duplicateReport(report)}
                    style={{
                      backgroundColor: "#6f42c1",
                      color: "white",
                      border: "none",
                      padding: "8px 12px",
                      borderRadius: "4px",
                      fontSize: "11px",
                      cursor: "pointer",
                      flex: 1
                    }}
                  >
                    📋 Copy
                  </button>
                  <button
                    onClick={() => deleteReport(report.id, report.title)}
                    style={{
                      backgroundColor: "#dc3545",
                      color: "white",
                      border: "none",
                      padding: "8px 12px",
                      borderRadius: "4px",
                      fontSize: "11px",
                      cursor: "pointer",
                      flex: 1
                    }}
                  >
                    🗑️ Delete
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ReportDashboard;

